import styles from './AddPaymentMethodCard.module.css';

import { useEffect, useMemo, useState } from 'react';

import { motion } from 'framer-motion';

import { ArrowRight, ChevronLeft, Home, Info, Landmark, X, Zap } from 'lucide-react';
import visaLogo from '../../../../assets/pages/payment/visa.png';
import mastercardLogo from '../../../../assets/pages/payment/mastercard.png';
import clsx from 'clsx';
import { useAuth } from '../../../../auth/Auth';
import { PreviewPaymentRewardsRequestPayment, TenantProcessingFee } from '../../../utils/types';
import { useModal } from '../../../hooks/modalContext';
import {
  getBuildingDetails,
  getProcessingFeeDetails,
  postAnalyticsEvent,
  previewPaymentRewards,
} from '../../../../auth/_requests';
import { DueDateTypeEnum } from '../../../utils/paymentUtils';

type GroupedFee = Pick<TenantProcessingFee, 'paymentMethodName' | 'cardCompanyTypeValue'> & {
  key: string;
  paymentMethodId: string;
  cardCompanyTypeId?: string | null;
  min: number;
  max: number;
  isFixedFee: boolean;
};

type PaymentCardVariant = 'pad' | 'debit' | 'visa' | 'mastercard' | 'default';
type FeePaymentPlan = 'direct' | 'autopay';
type PreviewPaymentType = 'direct' | 'subscription';
type RewardTotals = { totalPoints: number; totalTickets: number };
type PreviewBuildingContext = {
  amount: number;
  tenantBuildingId?: string;
  dueDateType?: DueDateTypeEnum;
};

const EXCLUDED_PAYMENT_METHODS = new Set(['Interac Instant', 'Cryptocurrency']);
const DEFAULT_PREVIEW_AMOUNT = 500;
const VALID_DUE_DATE_TYPES = new Set<string>(Object.values(DueDateTypeEnum));

function toPreviewPaymentType(feePaymentPlan: FeePaymentPlan): PreviewPaymentType {
  return feePaymentPlan === 'autopay' ? 'subscription' : 'direct';
}

function rewardMapKey(methodKey: string, paymentType: PreviewPaymentType): string {
  return `${methodKey}:${paymentType}`;
}

function formatRewardCount(value: number): string {
  return value.toLocaleString('en-US');
}

function isSupportedPaymentMethod(item: TenantProcessingFee): boolean {
  return (
    item.cardCompanyTypeValue !== 'Amex' && !EXCLUDED_PAYMENT_METHODS.has(item.paymentMethodName)
  );
}

function groupFees(fees: TenantProcessingFee[]): GroupedFee[] {
  const grouped = fees.reduce<Record<string, GroupedFee>>((acc, item) => {
    const key = `${item.paymentMethodName}-${item.cardCompanyTypeValue || ''}`;
    const isPreAuthorizedDebit = item.paymentMethodName === 'preAuthorizedDebit';

    if (!acc[key]) {
      acc[key] = {
        key,
        paymentMethodName: item.paymentMethodName,
        cardCompanyTypeValue: item.cardCompanyTypeValue,
        paymentMethodId: item.paymentMethodId,
        cardCompanyTypeId: item.cardCompanyTypeId,
        min: isPreAuthorizedDebit
          ? parseFloat(item.fixedProcessingFee || '0')
          : parseFloat(item.percentageRate),
        max: isPreAuthorizedDebit
          ? parseFloat(item.fixedProcessingFee || '0')
          : parseFloat(item.percentageRate),
        isFixedFee: isPreAuthorizedDebit,
      };
    } else if (isPreAuthorizedDebit) {
      const fixedFee = parseFloat(item.fixedProcessingFee || '0');
      acc[key].min = Math.min(acc[key].min, fixedFee);
      acc[key].max = Math.max(acc[key].max, fixedFee);
    } else {
      const rate = parseFloat(item.percentageRate);
      acc[key].min = Math.min(acc[key].min, rate);
      acc[key].max = Math.max(acc[key].max, rate);
    }

    return acc;
  }, {});

  const groupedArray = Object.values(grouped);
  groupedArray.sort((a, b) => paymentCardRank(a) - paymentCardRank(b));

  return groupedArray;
}

function buildGroupedFees(
  fees: TenantProcessingFee[],
  feePaymentPlan: FeePaymentPlan
): GroupedFee[] {
  const paymentType = toPreviewPaymentType(feePaymentPlan);
  const filteredData = fees.filter(
    (item) => item.paymentType === paymentType && isSupportedPaymentMethod(item)
  );

  return groupFees(filteredData);
}

function getUniquePreviewMethods(fees: TenantProcessingFee[]): GroupedFee[] {
  return groupFees(fees.filter(isSupportedPaymentMethod));
}

function buildPreviewPayment(
  method: GroupedFee,
  paymentType: PreviewPaymentType,
  previewContext: PreviewBuildingContext
): PreviewPaymentRewardsRequestPayment {
  const payment: PreviewPaymentRewardsRequestPayment = {
    paymentMethodId: method.paymentMethodId,
    paymentType,
    amount: previewContext.amount,
  };

  if (method.cardCompanyTypeId) {
    payment.cardCompanyTypeId = method.cardCompanyTypeId;
  }
  if (previewContext.tenantBuildingId) {
    payment.tenantBuildingId = previewContext.tenantBuildingId;
  }
  if (previewContext.dueDateType) {
    payment.dueDateType = previewContext.dueDateType;
  }

  return payment;
}

/** Highest rank among rows we order explicitly; anything unknown sorts right after (worst + 1). */
const PAYMENT_CARD_WORST_EXPLICIT_RANK = 4;

/**
 * Display order: Visa Debit, Mastercard Debit, Visa Credit, Mastercard Credit, then PAD.
 * Unknown methods use worst explicit rank + 1 so they follow all known types.
 */
function paymentCardRank(item: GroupedFee): number {
  const { paymentMethodName, cardCompanyTypeValue } = item;
  if (paymentMethodName === 'Debit Card' && cardCompanyTypeValue === 'Visa') return 0;
  if (paymentMethodName === 'Debit Card' && cardCompanyTypeValue === 'Mastercard') return 1;
  if (paymentMethodName === 'Credit Card' && cardCompanyTypeValue === 'Visa') return 2;
  if (paymentMethodName === 'Credit Card' && cardCompanyTypeValue === 'Mastercard') return 3;
  if (paymentMethodName === 'preAuthorizedDebit') return PAYMENT_CARD_WORST_EXPLICIT_RANK;
  return PAYMENT_CARD_WORST_EXPLICIT_RANK + 1;
}

function getPrimaryLabel(item: GroupedFee): string {
  if (item.paymentMethodName === 'preAuthorizedDebit') return 'Pre authorized debit (PAD)';
  if (item.paymentMethodName === 'Debit Card' && item.cardCompanyTypeValue) {
    return `${item.cardCompanyTypeValue} debit`;
  }
  if (item.paymentMethodName === 'Credit Card' && item.cardCompanyTypeValue) {
    return `${item.cardCompanyTypeValue} credit`;
  }
  return [item.paymentMethodName, item.cardCompanyTypeValue].filter(Boolean).join(' ');
}

function getIconVariant(item: GroupedFee): PaymentCardVariant {
  if (item.paymentMethodName === 'preAuthorizedDebit') return 'pad';
  if (item.cardCompanyTypeValue === 'Visa') return 'visa';
  if (item.cardCompanyTypeValue === 'Mastercard') return 'mastercard';
  if (item.paymentMethodName === 'Debit Card') return 'debit';
  return 'default';
}

function formatFeeValue(item: GroupedFee): string {
  if (item.isFixedFee) {
    return `$${item.min.toFixed(2)}`;
  }
  return `${item.min}%`;
}

function getFeeQualifier(item: GroupedFee): string {
  if (item.min !== item.max) {
    return 'starts at';
  }
  return 'flat';
}

/** Maps a fee row to the exact value the analytics API expects, per the Scalar docs. */
function getAnalyticsPaymentMethodValue(item: GroupedFee): string | null {
  if (item.paymentMethodName === 'preAuthorizedDebit') return 'preAuthorizedDebit';
  if (item.paymentMethodName === 'Debit Card' && item.cardCompanyTypeValue === 'Visa') {
    return 'visaDebit';
  }
  if (item.paymentMethodName === 'Debit Card' && item.cardCompanyTypeValue === 'Mastercard') {
    return 'mastercardDebit';
  }
  if (item.paymentMethodName === 'Credit Card' && item.cardCompanyTypeValue === 'Visa') {
    return 'visaCredit';
  }
  if (item.paymentMethodName === 'Credit Card' && item.cardCompanyTypeValue === 'Mastercard') {
    return 'mastercardCredit';
  }
  return null;
}

function isBestValueDebitCard(item: GroupedFee): boolean {
  return (
    item.paymentMethodName === 'Debit Card' &&
    (item.cardCompanyTypeValue === 'Visa' || item.cardCompanyTypeValue === 'Mastercard')
  );
}

function PaymentMethodIcon({
  variant,
  inactive = false,
}: {
  variant: PaymentCardVariant;
  inactive?: boolean;
}) {
  const size = 18;
  const strokeClass = clsx(styles.iconStrokePad, inactive && styles.iconInactive);
  switch (variant) {
    case 'pad':
      return <Home className={styles.iconStrokePad} size={size} strokeWidth={2} />;
    case 'debit':
      return <Landmark className={strokeClass} size={size} strokeWidth={2} />;
    case 'visa':
      return <img src={visaLogo} alt='Visa' className={styles.iconBrandLogoVisa} />;
    case 'mastercard':
      return (
        <img
          src={mastercardLogo}
          alt='Mastercard'
          className={styles.iconBrandLogoMastercard}
        />
      );
    default:
      return <Landmark className={strokeClass} size={size} strokeWidth={2} />;
  }
}

function iconCircleClass(variant: PaymentCardVariant): string {
  switch (variant) {
    case 'pad':
    case 'debit':
      return styles.iconCirclePad;
    case 'visa':
      return styles.iconCircleVisa;
    case 'mastercard':
      return styles.iconCircleMastercard;
    default:
      return styles.iconCirclePad;
  }
}

const AddPaymentMethodCard = () => {
  const [isOpen, setIsOpen] = useState(false);
  const { auth } = useAuth();
  const [rawFees, setRawFees] = useState<TenantProcessingFee[]>([]);
  const { setModal, modalState } = useModal();
  const payRentSetupFlow = modalState?.payRentSetupFlow === true;
  const [feePaymentPlan, setFeePaymentPlan] = useState<FeePaymentPlan>(() =>
    modalState?.feePaymentPlan === 'direct' ? 'direct' : 'autopay'
  );
  const [loading, setLoading] = useState(false);
  const [rewardsLoading, setRewardsLoading] = useState(false);
  const [rewardMap, setRewardMap] = useState<Record<string, RewardTotals>>({});
  const [previewContext, setPreviewContext] = useState<PreviewBuildingContext>({
    amount: DEFAULT_PREVIEW_AMOUNT,
  });

  const [selectedMethod, setSelectedMethod] = useState<GroupedFee | null>(null);

  const processingData = useMemo(
    () => buildGroupedFees(rawFees, feePaymentPlan),
    [rawFees, feePaymentPlan]
  );
  // Only one card should ever carry the "Best value" tag, even though Visa and
  // Mastercard debit currently earn identical points/tickets.
  const bestValueKey = useMemo(
    () => processingData.find(isBestValueDebitCard)?.key ?? null,
    [processingData]
  );
  const selectedPaymentType = toPreviewPaymentType(feePaymentPlan);

  const handleCardSelection = (method: GroupedFee) => {
    if (selectedMethod?.key === method.key) {
      setSelectedMethod(null);
      return;
    }
    setSelectedMethod(method);
    if (payRentSetupFlow) {
      const analyticsValue = getAnalyticsPaymentMethodValue(method);
      if (analyticsValue) {
        postAnalyticsEvent(auth?.data.token, 'paymentMethodTypeSelection', analyticsValue);
      }
    }
  };

  // Tracked only for the first-time signup flow (payRentSetupFlow), not when
  // adding/editing a payment method afterwards.
  const handleFeePlanChange = (plan: FeePaymentPlan) => {
    if (payRentSetupFlow && plan !== feePaymentPlan) {
      postAnalyticsEvent(
        auth?.data.token,
        'paymentMethodTransactionTypeSelection',
        toPreviewPaymentType(plan)
      );
    }
    setFeePaymentPlan(plan);
  };

  useEffect(() => {
    const getFeeDetails = async () => {
      try {
        setLoading(true);
        const token = auth?.data.token || '';
        const [feeRes, buildingRes] = await Promise.all([
          getProcessingFeeDetails(token),
          getBuildingDetails(token).catch(() => null),
        ]);

        setRawFees(feeRes.data);

        const firstBuilding = buildingRes?.data?.[0];
        const rentAmount = Number(firstBuilding?.building?.rentAmount);
        const dueDateTypeName = firstBuilding?.dueDateType?.name;

        setPreviewContext({
          amount:
            Number.isFinite(rentAmount) && rentAmount > 0 ? rentAmount : DEFAULT_PREVIEW_AMOUNT,
          tenantBuildingId: firstBuilding?.building?.tenantBuildingId,
          dueDateType: VALID_DUE_DATE_TYPES.has(dueDateTypeName ?? '')
            ? (dueDateTypeName as DueDateTypeEnum)
            : undefined,
        });
      } catch {
        console.error('Error getting payment processing fee details');
      } finally {
        setLoading(false);
      }
    };
    getFeeDetails();
  }, [auth?.data.token]);

  useEffect(() => {
    setSelectedMethod((current) => {
      if (current && processingData.some((item) => item.key === current.key)) {
        return current;
      }
      return processingData[0] ?? null;
    });
  }, [processingData]);

  useEffect(() => {
    const token = auth?.data.token;
    const methods = getUniquePreviewMethods(rawFees);
    if (!token || methods.length === 0) {
      setRewardMap({});
      return;
    }

    let cancelled = false;

    const loadRewards = async () => {
      try {
        setRewardsLoading(true);
        const paymentTypes: PreviewPaymentType[] = ['direct', 'subscription'];
        const payments = methods.flatMap((method) =>
          paymentTypes.map((paymentType) =>
            buildPreviewPayment(method, paymentType, previewContext)
          )
        );
        const res = await previewPaymentRewards(token, { payments });
        if (cancelled) return;

        const next: Record<string, RewardTotals> = {};
        payments.forEach((payment, index) => {
          const method = methods[Math.floor(index / paymentTypes.length)];
          const data = res.data?.[index];
          if (!method || !data) return;
          next[rewardMapKey(method.key, payment.paymentType)] = {
            totalPoints: data.totalPoints,
            totalTickets: data.totalTickets,
          };
        });
        setRewardMap(next);
      } catch {
        if (!cancelled) {
          setRewardMap({});
        }
      } finally {
        if (!cancelled) {
          setRewardsLoading(false);
        }
      }
    };

    loadRewards();
    return () => {
      cancelled = true;
    };
  }, [auth?.data.token, rawFees, previewContext]);

  return (
    <motion.main
      className={styles.main}
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
    >
      <div className={styles.headingOuter}>
        <div className='flex items-center gap-[12px]'>
          <button
            type='button'
            onClick={() =>
              payRentSetupFlow
                ? setModal('add-home', {
                    tenantBuildingId: modalState?.tenantBuildingId,
                    payRentSetupFlow: true,
                  })
                : setModal(null)
            }
          >
            <ChevronLeft stroke='#0E0E0B' />
          </button>
          <span>Add payment method</span>
        </div>
        {!payRentSetupFlow && (
          <X className={styles.crossIcon} size={24} onClick={() => setModal(null)} stroke='#0E0E0B' />
        )}
      </div>
      <div className='flex items-start gap-2'>
        <Info
          className='w-3.5 h-3.5 text-blue-500 mt-0.5 flex-shrink-0 cursor-pointer
             transition-colors hover:text-blue-600 hover:scale-110'
          onClick={() => setIsOpen(true)}
        />

        <p className={styles.infoText}>
          Fees vary by card. You’ll see your exact fee after adding it.
        </p>
      </div>
      <div className={styles.feeToggle} role='group' aria-label='Payment type for fee display'>
        <button
          type='button'
          className={clsx(
            styles.feeToggleBtn,
            feePaymentPlan === 'autopay' && styles.feeToggleBtnActive
          )}
          onClick={() => handleFeePlanChange('autopay')}
        >
          Autopay
          <span className={styles.moreBadge}>+MORE</span>
        </button>
        <button
          type='button'
          className={clsx(
            styles.feeToggleBtn,
            feePaymentPlan === 'direct' && styles.feeToggleBtnActive
          )}
          onClick={() => handleFeePlanChange('direct')}
        >
          One-time payment
        </button>
      </div>
      {feePaymentPlan === 'direct' && (
        <div className={styles.autopayNudge} role='note'>
          <Zap className={styles.autopayNudgeIcon} size={16} strokeWidth={2.25} aria-hidden />
          <div className={styles.autopayNudgeCopy}>
            <p className={styles.autopayNudgeText}>
              Autopay costs less, earn more points, much better chances of winning free rent,
              and includes free credit reporting. Paying manually skips all of these.{' '}
              <button
                type='button'
                className={styles.autopayNudgeAction}
                onClick={() => handleFeePlanChange('autopay')}
              >
                Switch to Autopay
              </button>
            </p>
          </div>
        </div>
      )}
      {isOpen && (
        <div className='fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50'>
          <div className='bg-white p-6 rounded-lg max-w-sm w-full'>
            <div className='flex justify-end'>
              <X
                className='cursor-pointer text-gray-500 hover:text-gray-700'
                size={14}
                onClick={() => setIsOpen(false)}
              />
            </div>
            <h2 className='text-lg font-semibold mb-4'>Why do processing fees vary?</h2>
            <p className='text-sm'>
              Processing fees depend on your card type and issuer. Premium cards typically have
              higher fees due to rewards and benefits programs.
            </p>
          </div>
        </div>
      )}

      <div className={styles.detailCards}>
        {loading ? (
          <>
            <div className='inputFieldLoading w-full ' />
            <div className='inputFieldLoading w-full' />
            <div className='inputFieldLoading w-full' />
            <div className='inputFieldLoading w-full' />
          </>
        ) : (
          <>
            {processingData.map((item) => {
              const iconVariant = getIconVariant(item);
              const feeValue = formatFeeValue(item);
              const feeQualifier = getFeeQualifier(item);
              const isSelected = selectedMethod?.key === item.key;
              const isBestValue = bestValueKey === item.key;
              const rewards = rewardMap[rewardMapKey(item.key, selectedPaymentType)];

              return (
                <div className={styles.cardOuter} key={item.key}>
                  {isBestValue && <span className={styles.bestValueBadge}>BEST VALUE</span>}
                  <div
                    role='button'
                    tabIndex={0}
                    className={clsx(
                      styles.card,
                      isSelected ? styles.cardActive : styles.cardInactive
                    )}
                    onClick={() => handleCardSelection(item)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        handleCardSelection(item);
                      }
                    }}
                  >
                    <div
                      className={clsx(
                        styles.iconCircle,
                        isSelected ? iconCircleClass(iconVariant) : styles.iconCircleInactive
                      )}
                    >
                      <PaymentMethodIcon
                        variant={iconVariant}
                        inactive={!isSelected}
                      />
                    </div>
                    <div className={styles.cardMiddle}>
                      <span className={styles.cardTitle}>{getPrimaryLabel(item)}</span>
                      <span className={styles.feeLine}>
                        Fee {feeQualifier} {feeValue}
                      </span>
                    </div>
                    <div className={styles.rewardChips}>
                      <span
                        className={clsx(styles.ptsChip, !isSelected && styles.ptsChipInactive)}
                      >
                        PTS +{rewardsLoading ? '-' : formatRewardCount(rewards?.totalPoints ?? 0)}
                      </span>
                      <span
                        className={clsx(
                          styles.ticketsChip,
                          !isSelected && styles.ticketsChipInactive
                        )}
                      >
                        TICKETS +{rewardsLoading ? '-' : (rewards?.totalTickets ?? 0)}
                      </span>
                    </div>
                  </div>
                </div>
              );
            })}
          </>
        )}
      </div>
      <button
        className={clsx(styles.nextBtn, selectedMethod && !loading && styles.submitBtn)}
        disabled={!selectedMethod || loading}
        type='submit'
        onClick={() => {
          const nextModalState = {
            tenantBuildingId: modalState?.tenantBuildingId,
            payRentSetupFlow: modalState?.payRentSetupFlow,
            feePaymentPlan,
          };
          if (selectedMethod?.paymentMethodName === 'preAuthorizedDebit') {
            setModal('pad-verification-method', nextModalState);
          } else {
            setModal('add-card', {
              paymentType: selectedMethod?.paymentMethodName,
              ...nextModalState,
            });
          }
        }}
      >
        <span>{'Next'}</span>
        <ArrowRight stroke={!selectedMethod || loading ? '#0E0E0B66' : '#fff'} />
      </button>
    </motion.main>
  );
};

export default AddPaymentMethodCard;
